// EnhancedShareDialog.jsx - Drop-in replacement for the share dialog in UnifiedPitchAllocator
// Supports both new clean URLs and legacy random ID URLs

import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

const EnhancedShareDialog = ({ 
  clubId,           // e.g., "23PKT8"
  clubName,         // e.g., "Eastern United FC"
  clubLogo,         // Club logo URL
  date,             // e.g., "2026-02-22" (YYYY-MM-DD)
  facilityName,     // e.g., "Main Ground" (optional)
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState('link');
  const [linkFormat, setLinkFormat] = useState('new'); // 'new' or 'legacy'
  const [linkType, setLinkType] = useState('date'); // 'date' or 'today'
  const [linkCopied, setLinkCopied] = useState(false);

  // Generate the different link formats
  const baseUrl = window.location.origin;
  
  // NEW format links (recommended)
  const newDateLink = `${baseUrl}/share/${clubId}/${date}`;
  const newTodayLink = `${baseUrl}/share/${clubId}/today`;
  
  // Determine which link to show based on user selection
  const getActiveLink = () => {
    if (linkFormat === 'new') {
      return linkType === 'date' ? newDateLink : newTodayLink;
    }
    // Legacy format would go here if you want to keep supporting it
    // For now, we'll just use the new format
    return newDateLink;
  };

  const activeLink = getActiveLink();

  const formatDate = (dateString) => {
    const d = new Date(dateString + 'T00:00:00');
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(activeLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const canvas = document.querySelector('#enhanced-qr-code-container canvas');
    if (!canvas) return;

    // Create a larger canvas for the final QR code with branding
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d');
    
    // Set final canvas size (larger for better print quality)
    const padding = 60;
    const qrSize = 400;
    const logoSize = 80;
    const textHeight = 120;
    finalCanvas.width = qrSize + (padding * 2);
    finalCanvas.height = qrSize + (padding * 2) + textHeight;

    // White background
    finalCtx.fillStyle = '#ffffff';
    finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

    // Draw QR code (scaled up from 200 to 400)
    finalCtx.drawImage(canvas, padding, padding, qrSize, qrSize);

    // Add club logo if available
    if (clubLogo) {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => {
        // Draw logo centered above QR code
        finalCtx.drawImage(
          logoImg,
          (finalCanvas.width - logoSize) / 2,
          20,
          logoSize,
          logoSize
        );
        downloadCanvas();
      };
      logoImg.onerror = () => {
        // If logo fails to load, just download without it
        downloadCanvas();
      };
      logoImg.src = clubLogo;
    } else {
      downloadCanvas();
    }

    function downloadCanvas() {
      // Add club name
      finalCtx.fillStyle = '#1f2937';
      finalCtx.font = 'bold 24px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      finalCtx.textAlign = 'center';
      finalCtx.fillText(clubName, finalCanvas.width / 2, qrSize + padding + 40);

      // Add instruction text
      finalCtx.fillStyle = '#6b7280';
      finalCtx.font = '18px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      finalCtx.fillText(
        'Scan to view match schedule',
        finalCanvas.width / 2,
        qrSize + padding + 70
      );

      // Add date if it's a date-specific link
      if (linkType === 'date') {
        finalCtx.fillStyle = '#9ca3af';
        finalCtx.font = '16px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        finalCtx.fillText(
          formatDate(date),
          finalCanvas.width / 2,
          qrSize + padding + 95
        );
      } else {
        finalCtx.fillStyle = '#22c55e';
        finalCtx.font = 'bold 16px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        finalCtx.fillText(
          'Always shows today\'s schedule',
          finalCanvas.width / 2,
          qrSize + padding + 95
        );
      }

      // Download
      const url = finalCanvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${clubName.replace(/\s+/g, '-')}-schedule-${linkType === 'date' ? date : 'today'}-qr.png`;
      link.href = url;
      link.click();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          maxWidth: '520px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#243665',
            margin: 0,
            textAlign: 'center'
          }}>
            Share Schedule
          </h2>
          
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginTop: '8px',
            marginBottom: 0,
            textAlign: 'center'
          }}>
            {facilityName && (
              <span style={{ display: 'block', fontWeight: '500', color: '#374151' }}>
                📍 {facilityName}
              </span>
            )}
            <span style={{ display: 'block', marginTop: '4px' }}>
              {formatDate(date)}
            </span>
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <button
            onClick={() => setActiveTab('link')}
            style={{
              flex: 1,
              padding: '14px 16px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'link' ? '3px solid #22c55e' : '3px solid transparent',
              color: activeTab === 'link' ? '#22c55e' : '#6b7280',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            🔗 Copy Link
          </button>
          <button
            onClick={() => setActiveTab('qr')}
            style={{
              flex: 1,
              padding: '14px 16px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'qr' ? '3px solid #22c55e' : '3px solid transparent',
              color: activeTab === 'qr' ? '#22c55e' : '#6b7280',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📱 QR Code
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {activeTab === 'link' ? (
            /* Link Tab */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Link Type Selection */}
              <div>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '12px'
                }}>
                  Choose link type:
                </label>
                
                {/* Date-Specific Option */}
                <div
                  onClick={() => setLinkType('date')}
                  style={{
                    padding: '16px',
                    border: linkType === 'date' ? '2px solid #22c55e' : '2px solid #e5e7eb',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    backgroundColor: linkType === 'date' ? '#f0fdf4' : 'white',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: '2px solid ' + (linkType === 'date' ? '#22c55e' : '#d1d5db'),
                      backgroundColor: linkType === 'date' ? '#22c55e' : 'white',
                      flexShrink: 0,
                      marginTop: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {linkType === 'date' && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: 'white',
                          borderRadius: '50%'
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1f2937',
                        marginBottom: '4px'
                      }}>
                        📅 Fixed date link
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        marginBottom: '8px'
                      }}>
                        Shows schedule for {formatDate(date)} only
                      </div>
                      <div style={{
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        color: '#9ca3af',
                        backgroundColor: '#f9fafb',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        wordBreak: 'break-all'
                      }}>
                        {newDateLink}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#059669',
                        marginTop: '8px',
                        fontWeight: '500'
                      }}>
                        ✓ Perfect for emails and team group chats
                      </div>
                    </div>
                  </div>
                </div>

                {/* Today Option */}
                <div
                  onClick={() => setLinkType('today')}
                  style={{
                    padding: '16px',
                    border: linkType === 'today' ? '2px solid #22c55e' : '2px solid #e5e7eb',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: linkType === 'today' ? '#f0fdf4' : 'white',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: '2px solid ' + (linkType === 'today' ? '#22c55e' : '#d1d5db'),
                      backgroundColor: linkType === 'today' ? '#22c55e' : 'white',
                      flexShrink: 0,
                      marginTop: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {linkType === 'today' && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: 'white',
                          borderRadius: '50%'
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1f2937',
                        marginBottom: '4px'
                      }}>
                        🔄 Dynamic "today" link
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        marginBottom: '8px'
                      }}>
                        Always shows today's schedule (updates daily)
                      </div>
                      <div style={{
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        color: '#9ca3af',
                        backgroundColor: '#f9fafb',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        wordBreak: 'break-all'
                      }}>
                        {newTodayLink}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#059669',
                        marginTop: '8px',
                        fontWeight: '500'
                      }}>
                        ✓ Perfect for QR codes and clubhouse posters
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Copy Link Button */}
              <div style={{
                display: 'flex',
                gap: '8px'
              }}>
                <input
                  type="text"
                  value={activeLink}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '13px',
                    backgroundColor: '#f9fafb',
                    color: '#374151',
                    fontFamily: 'monospace'
                  }}
                />
                <button
                  onClick={handleCopyLink}
                  style={{
                    padding: '12px 20px',
                    backgroundColor: linkCopied ? '#22c55e' : '#243665',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    minWidth: '100px',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {linkCopied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          ) : (
            /* QR Code Tab */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              {/* Link Type Toggle for QR */}
              <div style={{
                display: 'flex',
                gap: '8px',
                padding: '4px',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
                width: '100%'
              }}>
                <button
                  onClick={() => setLinkType('date')}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    backgroundColor: linkType === 'date' ? 'white' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: linkType === 'date' ? '#1f2937' : '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: linkType === 'date' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                >
                  📅 This Date
                </button>
                <button
                  onClick={() => setLinkType('today')}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    backgroundColor: linkType === 'today' ? 'white' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: linkType === 'today' ? '#1f2937' : '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: linkType === 'today' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                >
                  🔄 Today
                </button>
              </div>

              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0,
                textAlign: 'center'
              }}>
                {linkType === 'date' 
                  ? `QR code for ${formatDate(date)}`
                  : 'QR code that always shows today\'s schedule'
                }
              </p>
              
              {/* QR Code */}
              <div 
                id="enhanced-qr-code-container"
                style={{
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  border: '2px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              >
                <QRCodeCanvas
                  value={activeLink}
                  size={200}
                  level="M"
                  includeMargin={false}
                  style={{ display: 'block' }}
                />
              </div>

              {/* Download Button */}
              <button
                onClick={handleDownloadQR}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                ⬇️ Download QR Code
              </button>
            </div>
          )}
        </div>

        {/* Close button */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedShareDialog;
